
import React, { Component, useCallback, ComponentProps } from "react";
import { useField, useForm, Field } from "react-final-form";
import { observable, action, computed } from "mobx";
import { observer } from "mobx-react";
import { FieldValidator } from "final-form";

import { dig } from "@matchlighter/common_library/lib/macro";
import { observerMethod } from "@matchlighter/common_library/lib/decorators/method_component";
import { styleSheet } from "@matchlighter/common_library/lib/StyleSheet";
import { fetching_component, api_fetched } from "@matchlighter/fetcher"

import { View, Flex } from "@instructure/ui-layout";
import { Link } from "@instructure/ui-link";
import { Text } from "@instructure/ui-text";
import { Modal } from "@instructure/ui-modal";
import { Heading } from "@instructure/ui-heading";
import { IconInfoLine } from "@instructure/ui-icons";
import { FormField } from '@instructure/ui-form-field'
import { FormFieldGroup } from '@instructure/ui-form-field'
import { Button, CloseButton } from "@instructure/ui-buttons";
import { ScreenReaderContent } from '@instructure/ui-a11y-content'
import { TextInput } from "@instructure/ui-text-input";

import { AsyncMultiSelectAdapter, AsyncSelectAdapter, AsyncSelect, AsyncSelect2Adapter } from "./form/AsyncSelect";
import { TextInputAdapter, CheckBoxAdapter, FFAdapterComponent, FieldComponentProps } from "./final_form/FormAdapters";
import { composeValidators, isNotBlank, isAlphanumeric, looksLikeAnEmail, matches, isTruthy, isPresent, IDENTITY } from "./final_form/form_helpers";
import { SmartLink } from "./SmartLink";
import { User } from "@/models/user";
import { EducatorAssignment, District } from "@/models/common";
import { env } from "@/environment";
import { PrefixedField, FieldPrefix, usePrefixed, OnFieldChange, usePrefixedField, useFieldState } from "./final_form/components";
import { CampusSelector } from "./form/CampusSelect";
import { DistrictSelector } from "./form/DistrictSelect";

const styles = styleSheet(require('./DemographicForm.module.scss'));

const DependantCheckboxEvents: React.FC<{ name: string, dependsOn: string }> = ({ name, dependsOn }) => {
    const form = useForm();

    dependsOn += '_unavailable';

    const prefixedComp = usePrefixed(dependsOn);
    const changedCallback = useCallback((newValue, oldValue) => {
        if (newValue == false) form.change(prefixedComp, false)
    }, [form, prefixedComp])

    const prefixedName = usePrefixed(name);
    const depChangedCallback = useCallback((newValue, oldValue) => {
        if (newValue == true) form.change(prefixedName, true)
    }, [form, prefixedName])

    return <>
        <OnFieldChange name={name} children={changedCallback} />
        <OnFieldChange name={dependsOn} children={depChangedCallback} />
    </>
}

const DontHaveCheckbox: React.FC<FieldComponentProps<typeof CheckBoxAdapter> & { dependsOn?: string[] }> = ({ dependsOn, ...props }) => {
    return <>
        <PrefixedField component={CheckBoxAdapter}
            label="I don't have one"
            inline
            {...props}
        />
        {(dependsOn || []).map(k => <DependantCheckboxEvents name={props.name} dependsOn={k} key={k} />)}
    </>
}

const NoDemogField = <C extends FFAdapterComponent>({
    label, width, interaction, unavailable_key, dependsOn, validate, ...props
}: FieldComponentProps<C> & { dependsOn?: string[] }) => {
    const formIsNew = usePrefixedField('id', { format: IDENTITY }).input.value == null;
    const dontHaveKey = unavailable_key || `${props.name}_unavailable`;
    const pfxDontHaveKey = usePrefixed(dontHaveKey);

    const reNullable = true;
    const initialValue = usePrefixedField(props.name).meta.initial;
    const isNullable = reNullable || !initialValue;

    const dontHaveField = useField(pfxDontHaveKey, { initialValue: !formIsNew && initialValue == null });
    const doesntHaveChecked = isNullable && dontHaveField.input.value;

    const validator: FieldValidator<any> = (v, av, meta) => {
        if (isNullable && av[pfxDontHaveKey]) return undefined;
        if (validate) return validate(v, av, meta)
    }

    return <FormField label={label} id="">
        <Flex direction="row" alignItems="start">
            <View position="relative" display="inline-block" width={width}>
                <PrefixedField
                    validate={validator}
                    interaction={doesntHaveChecked ? 'disabled' : interaction}
                    {...props as any}
                />
            </View>
            {isNullable && <View margin="0 small">
                <div style={{ marginTop: '7px' }}>
                    <DontHaveCheckbox name={dontHaveKey} dependsOn={dependsOn} />
                </div>
            </View>}
        </Flex>
    </FormField>
}

const NAME_VALIDATOR = matches(/^[\w \-']*$/, 'Must be alphanumeric');
const REQ_NAME_VALIDATOR = composeValidators(isNotBlank, NAME_VALIDATOR);
const TEA_ID_VALIDATOR = matches(/^\d{10}$/, 'Must be a 10-digit number');
const EMAILS_MATCH_VALIDATOR: FieldValidator<string> = (value, allValues, meta) => (value || '').toLowerCase() != (allValues['email_address'] || '').toLowerCase() ? 'Emails must match' : undefined;
const VERIFY_EMAIL_VALIDATOR = composeValidators(looksLikeAnEmail, EMAILS_MATCH_VALIDATOR);

@fetching_component
@observer
export class DemographicFormFields extends Component<{ prefix?: string, user?: User, confirm_email?: boolean }> {

    @api_fetched(`api/v1/educator_assignments`)
    get educator_assignments(): EducatorAssignment[] {
        return env.educator_assignments;
    }

    render() {
        return <FieldPrefix prefix={this.props.prefix}>
            <View display="block" margin="x-large 0 medium">
                <Text size="small" as="h2" weight="normal" letterSpacing="expanded" color="primary" transform="uppercase">
                    Educator Information
                </Text>
            </View>

            {/* Basic Info */}
            <FormFieldGroup description={null} layout="columns" colSpacing="large" vAlign="top">
                <View display="block" margin="medium 0 0">
                    <PrefixedField name="first_name" component={TextInputAdapter}
                        renderLabel="First Name"
                        parse={IDENTITY}
                        validate={REQ_NAME_VALIDATOR}
                    />
                </View>
                <View display="block" margin="medium 0 0">
                    <PrefixedField name="middle_name" component={TextInputAdapter}
                        renderLabel="Middle Name"
                        parse={IDENTITY}
                        validate={NAME_VALIDATOR}
                    />
                </View>
                <View display="block" margin="medium 0 0">
                    <PrefixedField name="last_name" component={TextInputAdapter}
                        renderLabel="Last Name"
                        parse={IDENTITY}
                        validate={REQ_NAME_VALIDATOR}
                    />
                </View>
            </FormFieldGroup>

            <View display="block" margin="medium 0 0">
                <PrefixedField name="email_address" component={TextInputAdapter}
                    renderLabel="Email (This will be your Canvas login)"
                    width="65%"
                    parse={IDENTITY}
                    validate={looksLikeAnEmail}
                />
            </View>

            {this.props.confirm_email && <View display="block" margin="medium 0 0">
                <PrefixedField name="confirm_email_address" component={TextInputAdapter}
                    renderLabel="Email Verification"
                    width="65%"
                    parse={IDENTITY}
                    validate={VERIFY_EMAIL_VALIDATOR}
                />
            </View>}

            {/* TEA ID */}
            <View display="block" margin="medium 0 0">
                <TeaIdField user={this.props.user} />
            </View>

            {/* District, Campus & Assignment */}
            <DistrictCampusFields />
            <EducatorAssignmentsField options={this.educator_assignments} />
        </FieldPrefix>
    }
}

@observer
class TeaIdField extends Component<{ user: User }> {
    @observable teaIdModalVisible = false;

    @computed get sis_ids() {
        return dig(this.props.user.sis_ids) || [];
    }

    @action.bound
    toggleTeaIdModal() {
        this.teaIdModalVisible = !this.teaIdModalVisible;
    }

    @observerMethod
    renderTeaIdModal() {
        return <Modal
            open={this.teaIdModalVisible}
            onDismiss={this.toggleTeaIdModal}
            size="medium"
            label="Modal Dialog: Find Your TEA Uniq-ID"
            shouldCloseOnDocumentClick
        >
            <Modal.Header>
                <CloseButton
                    placement="end"
                    offset="small"
                    onClick={this.toggleTeaIdModal}
                    screenReaderLabel="Close"
                />
                <Heading>Find Your TEA ID</Heading>
            </Modal.Header>
            <Modal.Body>
                <Text color="primary">
                    <p>
                        You can view your Unique ID number (Uniq-ID) on your TEAL profile.
                        If you are an educator and do not yet have a TEAL profile, follow instructions at <SmartLink to="https://tea.texas.gov/WorkArea/linkit.aspx?LinkIdentifier=id&ItemID=2147512257" target="_blank">https://tea.texas.gov/WorkArea/linkit.aspx?LinkIdentifier=id&ItemID=2147512257</SmartLink> to create one.
                    </p>
                    <p>
                        To find your Unique ID on your TEAL user account:
                    </p>
                    <ol>
                        <li>
                            Log in at <SmartLink to="https://pryor.tea.state.tx.us/" target="_blank">https://pryor.tea.state.tx.us/</SmartLink> with your TEAL user name and password.
                        </li>
                        <li>Click <b>Edit My Profile</b> in the Self-Service menu on the left.</li>
                        <li>Look for the Uniq-ID label under the section with your name information. The ten-digit UID is next to it.</li>
                    </ol>
                    <p>
                        If there is no Uniq-ID, click on the Click Here link and fill in the SSN and other information required
                        to match your profile to your Uniq-ID.
                    </p>
                    <p>
                        Please note that your district, charter school, or employing organization also have your Unique ID number
                        on file. It is not the same number as your TEA ID, used for educator certification testing.
                    </p>
                </Text>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={this.toggleTeaIdModal} margin="0 x-small 0 0">Close</Button>
            </Modal.Footer>
        </Modal>
    }

    @observerMethod
    renderTEAIdLabel() {
        return <span className={styles('tea-info')}>
            <Text>
                TEA Uniq-ID
                &nbsp;
                <Link onClick={this.toggleTeaIdModal}>
                    <IconInfoLine />
                    <ScreenReaderContent>Show info for TEA Uniq-ID</ScreenReaderContent>
                </Link>
            </Text>
        </span>
    }

    renderDummyField() {
        return <TextInput
            renderLabel={this.renderTEAIdLabel()}
            width="25%"
            interaction="disabled"
            value={this.sis_ids.join(', ')}
        />
    }

    renderLiveField() {
        return <NoDemogField name="tea_id" component={TextInputAdapter}
            label={this.renderTEAIdLabel()}
            width="25%"
            renderLabel={<ScreenReaderContent>TEA Unique ID</ScreenReaderContent>}
            validate={TEA_ID_VALIDATOR}
        />
    }

    render() {
        const teaIdLocked = this.sis_ids.length > 0;
        return <>
            {this.renderTeaIdModal()}
            {teaIdLocked ? this.renderDummyField() : this.renderLiveField()}
        </>
    }
}

@observer
class EducatorAssignmentsField extends Component<{ options: EducatorAssignment[] }> {
    @computed
    get educator_assignments() {
        return this.props.options || [];
    }

    render() {
        return <>
            <View display="block" margin="medium 0 0">
                <PrefixedField name="educator_assignments" component={AsyncMultiSelectAdapter}
                    renderLabel="Current Assignment/Role"
                    width="65%"
                    options={this.educator_assignments}
                    validate={isPresent}
                />
            </View>
        </>
    }
}

@observer
class DistrictCampusFields extends Component {

    @observerMethod
    renderDistrictField() {
        return <NoDemogField name="district" component={AsyncSelect2Adapter}
            // unavailable_key="district_unavailable"
            ui_component={DistrictSelector}
            label="District / Charter"
            width="65%"
            validate={isTruthy}
        />
    }

    @observerMethod
    renderCampusField() {
        const districtField = useFieldState('district');
        const district: District = districtField.value;

        return <NoDemogField name="campus" component={AsyncSelectAdapter}
            // unavailable_key="district_unavailable"
            dependsOn={['district']}
            ui_component={CampusSelector}
            district={district}
            label="Campus"
            width="65%"
            validate={isTruthy}
            searchable={false}
        />
    }

    render() {
        return <>
            <View display="block" margin="medium 0 0">
                {this.renderDistrictField()}
            </View>
            <View display="block" margin="medium 0 0">
                {this.renderCampusField()}
            </View>
            <ResetCampusOnDistrictChange />
        </>
    }
}

const ResetCampusOnDistrictChange = ({ }) => {
    const form = useForm();
    const campusKey = usePrefixed('campus');
    const callback = useCallback((newValue, oldValue) => {
        if (!oldValue) return;
        if (newValue && newValue.id == oldValue.id) return;
        form.change(campusKey, null);
    }, [form, campusKey])
    return <OnFieldChange name="district" children={callback} />
}

export function serializeDemographicForm(values) {
    const serialized = {
        first_name: values.first_name, middle_name: values.middle_name, last_name: values.last_name,
        email_address: values.email_address,
        educator_assignment_ids: (values.educator_assignments || []).map(ea => ea.id),

        district_id: values.district_unavailable ? null : dig(values.district.id),
        campus_id: values.campus_unavailable ? null : dig(values.campus.id),
    }

    if (!values.tea_id_unavailable && values.tea_id !== undefined) {
        serialized['tea_id'] = values.tea_id;
    }

    return serialized;
}
